import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  GET_DATA_SOURCE_CUSTOM_FIELDS,
  GET_ACCOUNT_TAXONOMY,
  SET_DATA_SOURCE_CUSTOM_FIELDS,
} from '../../graphql';
import {
  GetDataSourceCustomFieldsResponse,
  GetAccountTaxonomyResponse,
  DataSourceCustomFieldInput,
} from '../../types/graphql';
import { Loader } from '../common/PageLoader';

interface DataSourceConfigProps {
  dataSourceId: string;
  accountId: string;
}

const inputClassName =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

type CustomFieldDraft = {
  taxonomyKey: string;
  sourceKind: 'page_meta' | 'rss_property';
  locator: string;
  valueDelimiter: string;
  isActive: boolean;
};

export function DataSourceConfig({ dataSourceId, accountId }: DataSourceConfigProps) {
  const [fields, setFields] = useState<CustomFieldDraft[]>([]);
  const [message, setMessage] = useState<string>('');

  const { data: fieldsData, loading: fieldsLoading } = useQuery<GetDataSourceCustomFieldsResponse>(
    GET_DATA_SOURCE_CUSTOM_FIELDS,
    {
      variables: { dataSourceId },
      fetchPolicy: 'cache-and-network',
    }
  );
  const { data: taxonomyData, loading: taxonomyLoading } = useQuery<GetAccountTaxonomyResponse>(
    GET_ACCOUNT_TAXONOMY,
    { variables: { accountId } }
  );

  const [saveCustomFields, { loading: savingFields }] = useMutation(SET_DATA_SOURCE_CUSTOM_FIELDS, {
    refetchQueries: [{ query: GET_DATA_SOURCE_CUSTOM_FIELDS, variables: { dataSourceId } }],
    awaitRefetchQueries: true,
    onCompleted: () => setMessage('Custom fields saved.'),
    onError: (err) => setMessage(`Failed to save custom fields: ${err.message}`),
  });

  useEffect(() => {
    setFields([]);
    setMessage('');
  }, [dataSourceId]);

  useEffect(() => {
    if (fieldsData?.dataSourceCustomFields) {
      setFields(
        fieldsData.dataSourceCustomFields.map((f) => ({
          // Prefer taxonomyKey; fall back to fieldName for older rows that predate the UI change.
          taxonomyKey: f.taxonomyKey || f.fieldName || '',
          sourceKind: f.sourceKind,
          locator: f.locator,
          valueDelimiter: f.valueDelimiter || '',
          isActive: f.isActive,
        }))
      );
    }
  }, [fieldsData, dataSourceId]);

  const accountTaxonomy = taxonomyData?.accountTaxonomy || [];

  const addField = () =>
    setFields((prev) => [
      ...prev,
      {
        taxonomyKey: '',
        sourceKind: 'page_meta',
        locator: '',
        valueDelimiter: '',
        isActive: true,
      },
    ]);

  const updateField = (index: number, patch: Partial<CustomFieldDraft>) =>
    setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  const removeField = (index: number) => setFields((prev) => prev.filter((_, i) => i !== index));

  const handleSaveFields = async () => {
    setMessage('');
    const payload: DataSourceCustomFieldInput[] = fields
      .map((f) => {
        const taxonomyKey = f.taxonomyKey.trim();
        return {
          // Engine still keys extracted values by fieldName; keep it aligned with the
          // taxonomy dimension so account taxonomy is the single source of identity.
          fieldName: taxonomyKey,
          sourceKind: f.sourceKind,
          locator: f.locator.trim(),
          isActive: f.isActive ?? true,
          taxonomyKey,
          valueDelimiter: f.valueDelimiter || null,
        };
      })
      .filter((f) => f.taxonomyKey && f.locator);
    await saveCustomFields({ variables: { dataSourceId, fields: payload } });
  };

  /** Dimensions already chosen by other rows — keep the list unique per feed. */
  const usedKeys = (exceptIndex: number) =>
    new Set(fields.map((f, i) => (i === exceptIndex ? '' : f.taxonomyKey.trim())).filter(Boolean));

  if (fieldsLoading || taxonomyLoading) {
    return <Loader label="Loading configuration" className="text-cmp-ink-2 text-sm p-4" />;
  }

  return (
    <div className="p-4 space-y-4 bg-form-dark border-t border-cmp-line">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-cmp-ink text-sm font-semibold">Custom Fields</h4>
          <p className="text-xs text-cmp-ink-2">
            Map a page meta tag or RSS property into an account taxonomy dimension.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={addField}
          disabled={accountTaxonomy.length === 0}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Field
        </Button>
      </div>

      {accountTaxonomy.length === 0 && (
        <p className="text-xs text-cmp-ink-2">
          No account taxonomy defined yet — set one up in the Taxonomy settings tab before adding
          custom fields.
        </p>
      )}

      {fields.length === 0 && accountTaxonomy.length > 0 && (
        <p className="text-xs text-cmp-ink-2">No custom fields.</p>
      )}

      <div className="space-y-2">
        {fields.map((field, index) => {
          const taken = usedKeys(index);
          const dimensionOptions = accountTaxonomy.filter(
            (dim) => !taken.has(dim.key) || dim.key === field.taxonomyKey
          );
          return (
            <div key={index} className="grid grid-cols-12 gap-2 items-center">
              <select
                value={field.taxonomyKey}
                onChange={(e) => updateField(index, { taxonomyKey: e.target.value })}
                className={`col-span-3 ${inputClassName}`}
              >
                <option value="" className="bg-form-dark">
                  Taxonomy dimension…
                </option>
                {dimensionOptions.map((dim) => (
                  <option key={dim.key} value={dim.key} className="bg-form-dark">
                    {dim.key}
                  </option>
                ))}
                {field.taxonomyKey &&
                  !accountTaxonomy.some((dim) => dim.key === field.taxonomyKey) && (
                    <option value={field.taxonomyKey} className="bg-form-dark">
                      {field.taxonomyKey} (missing)
                    </option>
                  )}
              </select>
              <select
                value={field.sourceKind}
                onChange={(e) =>
                  updateField(index, {
                    sourceKind: e.target.value as 'page_meta' | 'rss_property',
                  })
                }
                className={`col-span-2 ${inputClassName}`}
              >
                <option value="page_meta" className="bg-form-dark">
                  Page meta tag
                </option>
                <option value="rss_property" className="bg-form-dark">
                  RSS property
                </option>
              </select>
              <input
                placeholder={
                  field.sourceKind === 'page_meta' ? 'e.g. og:article:author' : 'e.g. dc:creator'
                }
                value={field.locator}
                onChange={(e) => updateField(index, { locator: e.target.value })}
                className={`col-span-4 ${inputClassName}`}
              />
              <input
                placeholder="Delimiter"
                title="Optional delimiter to split multiple values (e.g. ,)"
                value={field.valueDelimiter}
                onChange={(e) => updateField(index, { valueDelimiter: e.target.value })}
                className={`col-span-2 ${inputClassName}`}
              />
              <button
                onClick={() => removeField(index)}
                className="col-span-1 text-error hover:opacity-80 flex justify-center"
                title="Remove field"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSaveFields} disabled={savingFields || accountTaxonomy.length === 0}>
          {savingFields ? 'Saving...' : 'Save Custom Fields'}
        </Button>
      </div>

      {message && (
        <p className={message.startsWith('Failed') ? 'text-error text-sm' : 'text-success text-sm'}>
          {message}
        </p>
      )}
    </div>
  );
}
